import { Theme } from "@emotion/react";
import { Box, InputLabel, SxProps, TextField } from "@mui/material";
import useValidateInput, { ValidateOption } from "hooks/useValidateInput";
import {
  forwardRef,
  HTMLInputTypeAttribute,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { toHalfWidth } from "util/text";

export interface TextFieldComponentProps {
  sx?: SxProps<Theme>;
  sxBox?: SxProps<Theme>;
  sxInput?: SxProps<Theme>;
  sxRoot?: SxProps<Theme>;
  sxNotchedOutline?: SxProps<Theme>;
  sxInputDisable?: SxProps<Theme>;
  value?: string | number | null;
  multiline?: boolean;
  sxLabelProps?: SxProps<Theme>;
  sxTitle?: SxProps<Theme>;
  disabled?: boolean;
  placeholder?: string;
  title?: string;
  type?: HTMLInputTypeAttribute;
  validateOption?: ValidateOption;
  clearError?: boolean;
  onError?: (error: boolean, message: string) => void;
  onChangeText?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
  isHiddenError?: boolean;
  onInputText?: (e: any) => void;
  inputProps?: React.InputHTMLAttributes<unknown>;
  normalizeHalfWidth?: boolean;
}

export interface TextFieldRef {
  validate: () => boolean;
}

const defaultTextFieldStyles = {
  width: "100%",
};

const defaultStylesRoot = {
  borderRadius: "9px",
};

const defaultStylesNotchedOutline = {
  border: "1px solid var(--gray-250)",
};

const defaultStylesInput = {
  padding: "13.5px 14px",
  boxSizing: "border-box",
  height: "50px",
};

/**
 * InputAdd component renders an input field with an "Add" icon and a label.
 * @param {object} props - The properties object.
 * @param {SxProps<Theme>} [props.sx] - Optional MUI sx prop for styling the container.
 */
const TextFieldComponent = forwardRef<TextFieldRef, TextFieldComponentProps>(
  (
    {
      value,
      sx,
      sxRoot,
      sxInput,
      sxBox,
      sxTitle,
      sxNotchedOutline,
      sxInputDisable,
      sxLabelProps,
      disabled,
      title,
      validateOption,
      clearError,
      onChangeText,
      onError,
      isHiddenError,
      onBlur,
      onInputText,
      inputProps,
      normalizeHalfWidth = false,
      ...rest
    }: TextFieldComponentProps,
    ref,
  ) => {
    const {
      state: { error, message },
      handleChangeText,
    } = useValidateInput({
      ...validateOption,
      onChangeText,
      onError,
      clearError,
    });

    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

    const handleBeforeInput = useCallback(
      (event: React.FormEvent<HTMLDivElement>) => {
        if (!normalizeHalfWidth) return;
        const nativeEvent = event.nativeEvent as InputEvent;
        const inputData = nativeEvent.data;
        if (!inputData) return;

        const converted = toHalfWidth(inputData);
        if (converted === inputData) return;

        event.preventDefault();
        nativeEvent.preventDefault?.();

        const inputElement = inputRef.current;
        if (!inputElement) return;

        const start = inputElement.selectionStart ?? inputElement.value.length;
        const end = inputElement.selectionEnd ?? start;
        const newValue =
          inputElement.value.slice(0, start) + converted + inputElement.value.slice(end);
        inputElement.value = newValue;
        inputElement.setSelectionRange(start + converted.length, start + converted.length);

        const simulatedInput = new InputEvent("input", { bubbles: true });
        inputElement.dispatchEvent(simulatedInput);
      },
      [normalizeHalfWidth],
    );

    useEffect(() => {
      if (!normalizeHalfWidth) return;
      const inputElement = inputRef.current;
      if (!inputElement) return;
      const { length } = inputElement.value;
      inputElement.setSelectionRange(length, length);
    }, [normalizeHalfWidth, value]);

    useImperativeHandle(ref, () => ({
      validate() {
        return handleChangeText(String(value));
      },
    }));

    return (
      <Box sx={{ ...sxBox }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "56px", ...sxBox }}>
          {title ? (
            <InputLabel
              htmlFor={title}
              sx={{
                display: "flex",
                fontSize: "23px",
                color: "var(--black)",
                fontWeight: "medium",
                overflow: "unset",
                ...sxTitle,
              }}
            >
              {title}
            </InputLabel>
          ) : null}
          <TextField
            id={title}
            inputRef={inputRef}
            disabled={disabled}
            value={value}
            InputLabelProps={{
              sx: sxLabelProps,
            }}
            // helperText={isHiddenError ? "" : message}
            error={isHiddenError ? false : error}
            sx={{
              ...defaultTextFieldStyles,
              ...sx,
              boxSizing: "border-box",
              "& .MuiOutlinedInput-root": {
                ...defaultStylesRoot,
                ...sxRoot,
              } as keyof SxProps<Theme>,
              "& .MuiOutlinedInput-input": {
                ...defaultStylesInput,
                ...sxInput,
              } as keyof SxProps<Theme>,
              "& .MuiOutlinedInput-notchedOutline": {
                ...defaultStylesNotchedOutline,
                ...sxNotchedOutline,
              } as keyof SxProps<Theme>,
              "& .MuiOutlinedInput-root .Mui-disabled": {
                ...sxInputDisable,
              } as keyof SxProps<Theme>,
              "& MuiOutlinedInput-input .Mui-disabled": {
                ...sxInputDisable,
              } as keyof SxProps<Theme>,
            }}
            onChange={(e) =>
              handleChangeText(normalizeHalfWidth ? toHalfWidth(e.target.value) : e.target.value)
            }
            onBlur={(e) => onBlur?.(e)}
            onInput={(e) => onInputText?.(e)}
            onBeforeInput={normalizeHalfWidth ? handleBeforeInput : undefined}
            inputProps={inputProps}
            autoComplete="off"
            {...rest}
          />
        </Box>
        {!isHiddenError && message && (
          <Box
            sx={{
              ml: "auto",
              mt: "2px",
              mb: "6px",
              color: "var(--red-800)",
              textAlign: "left",
              fontSize: { xs: "16px", md: "18px", xl: "20px" },
              ...sx,
            }}
          >
            {message}
          </Box>
        )}
      </Box>
    );
  },
);

export default TextFieldComponent;
